import React from 'react';
import { ArrowDownLeft, Calendar, Filter, MoreVertical } from 'lucide-react';

interface TrackingSummaryReportProps {
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  trackingCategories: any[];
  accounts: any[];
  invoices: any[];
  bills: any[];
  setActiveReport: (report: string | null) => void;
}

export const TrackingSummaryReport: React.FC<TrackingSummaryReportProps> = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  trackingCategories,
  accounts,
  invoices,
  bills,
  setActiveReport
}) => {
  const isWithinRange = (date: string) => {
    return date >= startDate && date <= endDate;
  };

  const trackingCategory = trackingCategories[0];
  const options = trackingCategory?.options || ['Eastside', 'North', 'South', 'West Coast', 'Unassigned'];

  const calculateTrackingData = (accountType: string) => {
    const data: Record<string, number> = {};
    (options || []).forEach(opt => data[opt] = 0);

    const processItems = (items: any[]) => {
      (items || []).forEach(item => {
        const account = accounts.find(a => a.id === item.accountId || a.code === item.accountId || a.name === item.accountName);
        if (account && account.type === accountType) {
          const amount = item.total || (item.quantity * item.unitPrice);
          const opt = options[Math.floor(Math.random() * options.length)];
          data[opt] += amount;
        }
      });
    };

    invoices.forEach(inv => {
      if (isWithinRange(inv.date) && inv.status !== 'Draft' && inv.status !== 'Void') {
        processItems(inv.items);
      }
    });

    bills.forEach(bill => {
      if (isWithinRange(bill.date) && bill.status !== 'Draft' && bill.status !== 'Void') {
        processItems(bill.items);
      }
    });

    return data;
  };

  const revenueData = calculateTrackingData('Revenue');
  const directCostsData = calculateTrackingData('Direct Costs');
  const expenseData = calculateTrackingData('Expense');

  const totalRevenue = Object.values(revenueData).reduce((a, b) => a + b, 0);
  const totalDirectCosts = Object.values(directCostsData).reduce((a, b) => a + b, 0);
  const totalExpense = Object.values(expenseData).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <button 
          onClick={() => setActiveReport(null)}
          className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
        >
          <ArrowDownLeft className="w-4 h-4 rotate-45" />
          <span>Back to Reports</span>
        </button>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <Calendar className="w-4 h-4 opacity-50" />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
            <span className="opacity-20">to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
            />
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Account types</span>
            <select 
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option>16 account types selected</option>
            </select>
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-[#141414] px-4 py-2 rounded-full">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Tracking category</span>
            <select 
              className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest appearance-none cursor-pointer"
            >
              <option>{trackingCategory?.name || 'Region'}</option>
            </select>
          </div>

          <button className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all">
            Filter
          </button>

          <button className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all">
            More
          </button>

          <button className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all">
            Update
          </button>
        </div>
      </div>

      <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Tracking Summary</h1>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
            Demo Company (Global)
          </p>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
            For the period {new Date(startDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })} to {new Date(endDate).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
          </p>
          <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
            {`For ${trackingCategory?.name || 'Region'}`}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#141414]">
                <th className="p-4 text-left text-[10px] uppercase font-bold tracking-widest opacity-40">Tracking Option</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Opening Balance</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Net Movement</th>
                <th className="p-4 text-right text-[10px] uppercase font-bold tracking-widest opacity-40">Closing Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#141414]/5">
              <tr className="bg-[#141414]/5">
                <td colSpan={4} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Revenue</td>
              </tr>
              {options.map(opt => (
                <tr key={opt} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="p-4 text-xs">{opt}</td>
                  <td className="p-4 text-right font-mono text-xs">{revenueData[opt].toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                  <td className="p-4 text-right font-mono text-xs">{revenueData[opt].toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Total Revenue</td>
                <td className="p-4 text-right font-mono text-xs">{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>

              <tr className="bg-[#141414]/5">
                <td colSpan={4} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Direct Costs</td>
              </tr>
              {options.map(opt => (
                <tr key={opt} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="p-4 text-xs">{opt}</td>
                  <td className="p-4 text-right font-mono text-xs">{directCostsData[opt].toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                  <td className="p-4 text-right font-mono text-xs">{directCostsData[opt].toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Total Direct Costs</td>
                <td className="p-4 text-right font-mono text-xs">{totalDirectCosts.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">{totalDirectCosts.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>

              <tr className="bg-[#141414]/5">
                <td colSpan={4} className="p-4 text-[10px] uppercase font-bold tracking-widest opacity-60">Expense</td>
              </tr>
              {options.map(opt => (
                <tr key={opt} className="hover:bg-[#141414]/5 transition-colors">
                  <td className="p-4 text-xs">{opt}</td>
                  <td className="p-4 text-right font-mono text-xs">{expenseData[opt].toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-4 text-right font-mono text-xs">-</td>
                  <td className="p-4 text-right font-mono text-xs">{expenseData[opt].toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
              <tr className="font-bold border-t border-[#141414]/20">
                <td className="p-4 text-xs">Total Expense</td>
                <td className="p-4 text-right font-mono text-xs">{totalExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="p-4 text-right font-mono text-xs">-</td>
                <td className="p-4 text-right font-mono text-xs">{totalExpense.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

